import { useState } from "react";
import * as api from "../../../../utils/api/deliveryDocumentation";
import { Button, XCircle } from "../../../ui";
import { AdminStatus } from "../AdminUi";
import SendAttempts from "./SendAttempts";
import { useAppLabel } from "../../../../utils/useAppLabel";

const STATUS = {
  draft: ["pending", "مسودة"],
  generated: ["manual", "جاهز للإرسال"],
  sending: ["pending", "قيد الإرسال"],
  sent: ["done", "المعترض عنها"],
  failed: ["rejected", "فشل / قابل للإعادة"],
};

const EMAIL_STATUS = {
  pending: ["pending", "البريد قيد المحاولة"],
  sent: ["done", "تم إرسال البريد"],
  failed: ["rejected", "فشل إرسال البريد"],
};

function value(v, fallback = "—") {
  return v == null || v === "" ? fallback : v;
}

function fmt(value) {
  return value ? new Date(value).toLocaleString("ar-SA") : "—";
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = api.ensureExcelFilename(filename, "objection-report.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function StatusBadge({ status }) {
  const [klass, label] = STATUS[status] || ["pending", status || "غير معروف"];
  return <AdminStatus status={klass}>{label}</AdminStatus>;
}

function EmailStatusBadge({ status }) {
  const [klass, label] = EMAIL_STATUS[status] || ["pending", "لم يبدأ إرسال البريد"];
  return <AdminStatus status={klass}>{label}</AdminStatus>;
}

export default function DisputeComposer({ initialDispute, onChange, onClose }) {
  const appLabel = useAppLabel();
  const [dispute, setDispute] = useState(initialDispute);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const generated = dispute?.generatedExcel;
  const requiresRegeneration =
    dispute?.requiresRegeneration ||
    Boolean(generated && dispute?.validationWarnings?.length);
  const canGenerate = ["draft", "failed", "generated"].includes(
    dispute?.status,
  );
  const canSend =
    (["generated", "failed"].includes(dispute?.status) ||
      (dispute?.status === "sent" && dispute?.emailStatus !== "sent")) &&
    !dispute?.validationWarnings?.length &&
    !requiresRegeneration;

  async function refresh(next) {
    setDispute(next);
    onChange?.(next);
  }

  async function act(name, fn) {
    setBusy(name);
    setError("");
    try {
      const data = await fn(dispute.id);
      await refresh(data.dispute);
    } catch (err) {
      setError(err.message || "تعذر إكمال العملية");
      try {
        const data = await api.getAdminDispute(dispute.id);
        await refresh(data.dispute);
      } catch (_) {}
    } finally {
      setBusy("");
    }
  }

  async function downloadGeneratedExcel(event) {
    event.preventDefault();
    setError("");
    try {
      const result = await api.downloadGeneratedDisputeExcel(dispute.id);
      saveBlob(
        result.blob,
        result.filename || generated?.originalName || generated?.name || "objection-report.xlsx",
      );
    } catch (err) {
      setError(err.message || "تعذر تحميل ملف Excel");
    }
  }

  if (!dispute) return null;
  const orders = dispute.orderSnapshot || [];
  const exceptions = orders.filter(
    (order) => order.manualMatch || order.evidenceException,
  );
  const providerLimits = dispute.providerLimits || {};
  return (
    <div
      className="matching-modal dispute-composer-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="matching-sheet dispute-composer-sheet">
        <div className="admin-record-header">
          <div>
            <b>إنشاء وإرسال الاعتراض</b>
            <span>
              {value(
                appLabel(
                  dispute.settingsSnapshot?.applicationSnapshot?.app,
                  dispute.settingsSnapshot?.applicationSnapshot?.name,
                ),
              )}{" "}
              · {orders.length} طلب
            </span>
          </div>
          <div className="dispute-chip-list">
            <StatusBadge status={dispute.status} />
            <EmailStatusBadge status={dispute.emailStatus} />
          </div>
        </div>

        <div className="admin-analytics dispute-stats">
          <div className="admin-stat">
            <span>التطبيق</span>
            <b>
              {value(
                appLabel(
                  dispute.app,
                  dispute.settingsSnapshot?.applicationSnapshot?.name,
                ),
              )}
            </b>
          </div>
          <div className="admin-stat">
            <span>المطعم</span>
            <b>{value(dispute.store)}</b>
          </div>
          <div className="admin-stat">
            <span>الفرع</span>
            <b>{value(dispute.branch, "بدون فرع")}</b>
          </div>
          <div className="admin-stat">
            <span>الطلبات</span>
            <b>{orders.length}</b>
          </div>
        </div>

        {!!dispute.validationWarnings?.length && (
          <div className="admin-note danger">
            {dispute.validationWarnings.map((warning) => (
              <div key={`${warning.rowId || ""}-${warning.code}`}>
                {warning.message}
              </div>
            ))}
          </div>
        )}
        {error && <div className="admin-export-feedback error">{error}</div>}
        {dispute.lastSendError?.message && (
          <div className="admin-export-feedback error">
            آخر فشل: {dispute.lastSendError.message}
          </div>
        )}
        {requiresRegeneration && (
          <div className="admin-export-feedback generating">
            تغيّرت بيانات مؤثرة بعد توليد الملف. أعد توليد Excel قبل الإرسال.
          </div>
        )}

        <section className="admin-filter-block">
          <div className="admin-panel-title">
            <h2>معاينة البريد</h2>
            <span>القيم محفوظة من لحظة إنشاء المسودة</span>
          </div>
          <div className="dispute-email-preview">
            <div>
              <b>To</b>
              <span>{(dispute.to || []).join("، ") || "—"}</span>
            </div>
            <div>
              <b>CC</b>
              <span>{(dispute.cc || []).join("، ") || "—"}</span>
            </div>
            <div>
              <b>Subject</b>
              <span>{value(dispute.subject)}</span>
            </div>
            <pre>{value(dispute.body)}</pre>
          </div>
        </section>

        <section className="admin-filter-block">
          <div className="admin-panel-title">
            <h2>الطلبات المحددة</h2>
            <span>المسودة غير قابلة لتغيير العضوية بعد إنشائها</span>
          </div>
          <div className="dispute-chip-list">
            {orders.map((order) => (
              <small key={order.rowId}>
                {value(order.orderNumber || order.last4 || order.orderId)}
              </small>
            ))}
          </div>
          {!!exceptions.length && (
            <div className="admin-note">
              توجد طلبات بمطابقة يدوية أو استثناء توثيق. سيظهر ذلك في التصدير
              وسجل النشاط.
            </div>
          )}
        </section>

        <section className="admin-filter-block">
          <div className="admin-panel-title">
            <h2>الملف المولد</h2>
            <span>
              {generated
                ? `${generated.recordCount || 0} صف`
                : "لم يتم توليد Excel بعد"}
            </span>
          </div>
          {generated ? (
            <div className="dispute-generated-file">
              <a
                className="admin-inline-button"
                href={generated.url}
                onClick={downloadGeneratedExcel}
              >
                {generated.originalName || generated.name || "فتح Excel"}
              </a>
              <small>تم التوليد: {fmt(generated.generatedAt)}</small>
              {requiresRegeneration && (
                <AdminStatus status="pending">يتطلب إعادة توليد</AdminStatus>
              )}
            </div>
          ) : (
            <div className="admin-empty">
              سيتم توليد ملف Excel من الخادم للطلبات المحددة فقط.
            </div>
          )}
        </section>

        <section className="admin-filter-block">
          <div className="admin-panel-title">
            <h2>الأدلة والمرفقات</h2>
            <span>
              {dispute.evidenceManifest?.length || 0} رابط آمن ·{" "}
              {providerLimits.strategy === "secure-links"
                ? "الإرسال بالروابط"
                : "حسب إعدادات المزود"}
            </span>
          </div>
          <div className="dispute-chip-list">
            {(dispute.evidenceManifest || []).map((item) => (
              <small
                key={item.id || `${item.documentationOrderId}-${item.evidenceType}`}
              >
                {item.evidenceType} · {value(item.originalName, "دليل")}
              </small>
            ))}
          </div>
          <div className="admin-note">
            يتم إرسال الأدلة كرابط آمن مع ملف Excel لتجنب حدود حجم البريد وعدد
            المرفقات.
          </div>
        </section>

        <SendAttempts attempts={dispute.attempts} />

        {!!exceptions.length && (
          <section className="admin-filter-block">
            <div className="admin-panel-title">
              <h2>مطابقات واستثناءات</h2>
              <span>{exceptions.length} عنصر يحتاج ظهور واضح في السجل</span>
            </div>
            <div className="dispute-exception-list">
              {exceptions.map((order) => (
                <div key={order.rowId || order.orderId} className="admin-note">
                  <b>{value(order.orderNumber || order.last4 || order.orderId)}</b>
                  {order.manualMatch && <div>مطابقة يدوية</div>}
                  {order.evidenceException && (
                    <>
                      <div>استثناء توثيق: {value(order.evidenceExceptionReason)}</div>
                      <div>الملاحظة: {value(order.evidenceExceptionNote)}</div>
                      <div>وقت الاعتماد: {fmt(order.evidenceExceptionAt)}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="btns">
          <Button
            variant="danger"
            appearance="soft"
            type="button"
            disabled={!!busy}
            onClick={onClose}
            icon={<XCircle size={13}/>}/>
          <Button
            variant="primary"
            appearance="outlined"
            type="button"
            disabled={!canGenerate || !!busy}
            loading={busy === "generate"}
            onClick={() => act("generate", api.generateAdminDispute)}
          >
            {generated
                ? "إعادة توليد Excel"
                : "توليد Excel"}
          </Button>
          <Button
            variant="primary"
            appearance="filled"
            type="button"
            disabled={!canSend || !!busy}
            loading={busy === "send"}
            onClick={() => act("send", api.sendAdminDispute)}
          >
            {dispute.status === "failed" || (dispute.status === "sent" && dispute.emailStatus !== "sent")
                ? "إعادة الإرسال"
                : "إرسال الاعتراض"}
          </Button>
        </div>
      </div>
    </div>
  );
}
